import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Heart, Plus, Trash2, Edit, Check, X, Users, Calendar, FileText, DollarSign, TrendingUp, Ban, CheckCircle, Mail, MailOpen, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [healers, setHealers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [healerTypes, setHealerTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Revenue & Analytics
  const [revenue, setRevenue] = useState(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  
  // User Management
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Email Management
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailTypeFilter, setEmailTypeFilter] = useState('');

  // Concerns Management
  const [concerns, setConcerns] = useState([]);
  const [loadingConcerns, setLoadingConcerns] = useState(false);
  const [showConcernDialog, setShowConcernDialog] = useState(false);
  const [editingConcern, setEditingConcern] = useState(null);
  const [concernForm, setConcernForm] = useState({
    concern: '',
    keywords: '',
    modalities_text: '',
    is_active: true,
  });
  
  // Add/Edit Healer Dialog
  const [showHealerDialog, setShowHealerDialog] = useState(false);
  const [editingHealer, setEditingHealer] = useState(null);
  const [healerForm, setHealerForm] = useState({
    healer_type: '',
    bio: '',
    specialties: '',
    languages: '',
    insurance_accepted: '',
    location: '',
    hourly_rate: '',
    years_experience: '',
    profile_image: '',
    credentials: ''
  });

  // Application Detail Dialog
  const [selectedApplication, setSelectedApplication] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, healersRes, appsRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/healers?show_all=true`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/healer-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/healer-types`)
      ]);
      
      setStats(statsRes.data);
      setHealers(healersRes.data);
      setApplications(appsRes.data);
      setHealerTypes(typesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenue = async () => {
    setLoadingRevenue(true);
    try {
      const response = await axios.get(`${API}/admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenue(response.data);
    } catch (error) {
      toast.error('Failed to load revenue data');
    } finally {
      setLoadingRevenue(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = {};
      if (userRoleFilter) params.role = userRoleFilter;
      if (userSearch) params.search = userSearch;
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchEmails = async () => {
    setLoadingEmails(true);
    try {
      const params = {};
      if (emailTypeFilter) params.email_type = emailTypeFilter;
      const response = await axios.get(`${API}/admin/emails`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEmails(response.data);
    } catch (error) {
      toast.error('Failed to load emails');
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleMarkEmailSent = async (emailId) => {
    try {
      await axios.put(`${API}/admin/emails/${emailId}/mark-sent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email marked as sent');
      fetchEmails();
    } catch (error) {
      toast.error('Failed to update email');
    }
  };

  // ---- Concerns (rules-based search) ----
  const fetchConcerns = async () => {
    setLoadingConcerns(true);
    try {
      const res = await axios.get(`${API}/admin/concerns`, { headers: { Authorization: `Bearer ${token}` } });
      setConcerns(res.data);
    } catch (err) {
      toast.error('Failed to load concerns');
    } finally {
      setLoadingConcerns(false);
    }
  };

  const openConcernDialog = (concern) => {
    if (concern) {
      setEditingConcern(concern);
      setConcernForm({
        concern: concern.concern,
        keywords: (concern.keywords || []).join(', '),
        modalities_text: (concern.modalities || []).map(m => `${m.modality} | ${m.weight} | ${m.reason || ''}`).join('\n'),
        is_active: concern.is_active !== false,
      });
    } else {
      setEditingConcern(null);
      setConcernForm({ concern: '', keywords: '', modalities_text: '', is_active: true });
    }
    setShowConcernDialog(true);
  };

  const parseModalities = (text) => {
    const lines = (text || '').split('\n').map(l => l.trim()).filter(Boolean);
    const out = [];
    for (const line of lines) {
      const parts = line.split('|').map(p => p.trim());
      const modality = parts[0];
      if (!modality) continue;
      const weight = Math.max(1, Math.min(3, parseInt(parts[1] || '2', 10) || 2));
      const reason = parts[2] || '';
      out.push({ modality, weight, reason });
    }
    return out;
  };

  const handleSaveConcern = async (e) => {
    e.preventDefault();
    const body = {
      concern: concernForm.concern.trim(),
      keywords: concernForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      modalities: parseModalities(concernForm.modalities_text),
      is_active: concernForm.is_active,
    };
    if (!body.concern) {
      toast.error('Concern name is required');
      return;
    }
    if (body.modalities.length === 0) {
      toast.error('Add at least one modality (format: "Modality | weight | reason")');
      return;
    }
    try {
      if (editingConcern) {
        await axios.put(`${API}/admin/concerns/${editingConcern.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Concern updated');
      } else {
        await axios.post(`${API}/admin/concerns`, body, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Concern added');
      }
      setShowConcernDialog(false);
      fetchConcerns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save concern');
    }
  };

  const handleDeleteConcern = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/concerns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Concern deleted');
      fetchConcerns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSuspendUser = async (userId, suspend) => {
    if (!window.confirm(`Are you sure you want to ${suspend ? 'suspend' : 'activate'} this user?`)) return;

    try {
      await axios.put(`${API}/admin/users/${userId}/suspend?suspended=${suspend}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${suspend ? 'suspended' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleAddHealer = () => {
    setEditingHealer(null);
    setHealerForm({
      healer_type: '',
      bio: '',
      specialties: '',
      languages: '',
      insurance_accepted: '',
      location: '',
      hourly_rate: '',
      years_experience: '',
      profile_image: '',
      credentials: ''
    });
    setShowHealerDialog(true);
  };

  const handleEditHealer = (healer) => {
    setEditingHealer(healer);
    setHealerForm({
      healer_type: healer.healer_type,
      bio: healer.bio,
      specialties: healer.specialties?.join(', ') || '',
      languages: healer.languages?.join(', ') || '',
      insurance_accepted: healer.insurance_accepted?.join(', ') || '',
      location: healer.location,
      hourly_rate: healer.hourly_rate,
      years_experience: healer.years_experience,
      profile_image: healer.profile_image || '',
      credentials: healer.credentials || ''
    });
    setShowHealerDialog(true);
  };

  const handleSaveHealer = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...healerForm,
        specialties: healerForm.specialties.split(',').map(s => s.trim()).filter(Boolean),
        languages: healerForm.languages.split(',').map(l => l.trim()).filter(Boolean),
        insurance_accepted: healerForm.insurance_accepted.split(',').map(i => i.trim()).filter(Boolean),
        hourly_rate: parseFloat(healerForm.hourly_rate),
        years_experience: parseInt(healerForm.years_experience)
      };

      if (editingHealer) {
        await axios.put(`${API}/admin/healers/${editingHealer.user_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Healer updated successfully');
      } else {
        await axios.post(`${API}/admin/healers`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Healer added successfully');
      }

      setShowHealerDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save healer');
    }
  };

  const handleDeleteHealer = async (healerId) => {
    if (!window.confirm('Are you sure you want to delete this healer?')) return;

    try {
      await axios.delete(`${API}/admin/healers/${healerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Healer deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete healer');
    }
  };

  const handleToggleLifetime = async (healerId, currentStatus) => {
    const action = currentStatus === 'lifetime' ? 'revoke lifetime access for' : 'grant lifetime free access to';
    if (!window.confirm(`Are you sure you want to ${action} this healer?`)) return;

    try {
      const response = await axios.put(`${API}/admin/healers/${healerId}/lifetime-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to update lifetime access');
    }
  };

  const handleApproveHealer = async (healerId, approved) => {
    try {
      await axios.put(`${API}/admin/healers/${healerId}/approve?approved=${approved}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Healer ${approved ? 'approved' : 'unapproved'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const handleUpdateApplicationStatus = async (appId, status) => {
    try {
      await axios.put(`${API}/healer-applications/${appId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application status updated');
      setSelectedApplication(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  const handleDeleteApplication = async (appId) => {
    if (!window.confirm('Delete this application?')) return;

    try {
      await axios.delete(`${API}/healer-applications/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application deleted');
      setSelectedApplication(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      {/* Navbar */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Heart className="w-8 h-8 text-#8B4513" />
            <span className="text-2xl font-bold text-gray-900">Ourmend Admin</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <Button variant="outline" onClick={logout} data-testid="logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Healers</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_healers || 0}</p>
              </div>
              <Users className="w-10 h-10 text-#8B4513" />
            </div>
          </Card>
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Approved</p>
                <p className="text-3xl font-bold text-green-600">{stats?.approved_healers || 0}</p>
              </div>
              <Check className="w-10 h-10 text-green-600" />
            </div>
          </Card>
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">{stats?.pending_applications || 0}</p>
              </div>
              <FileText className="w-10 h-10 text-yellow-600" />
            </div>
          </Card>
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.total_bookings || 0}</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="p-6">
          <Tabs defaultValue="healers">
            <TabsList>
              <TabsTrigger value="healers" data-testid="healers-tab">Healers</TabsTrigger>
              <TabsTrigger value="applications" data-testid="applications-tab">
                Applications ({applications.filter(a => a.status === 'pending').length})
              </TabsTrigger>
              <TabsTrigger value="revenue" data-testid="revenue-tab" onClick={fetchRevenue}>
                Revenue & Analytics
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="users-tab" onClick={fetchUsers}>
                User Management
              </TabsTrigger>
              <TabsTrigger value="emails" data-testid="emails-tab" onClick={fetchEmails}>
                Emails
              </TabsTrigger>
              <TabsTrigger value="concerns" data-testid="concerns-tab" onClick={fetchConcerns}>
                Search Concerns
              </TabsTrigger>
            </TabsList>

            <TabsContent value="healers" className="mt-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Manage Healers</h2>
                <Button
                  onClick={handleAddHealer}
                  className="bg-#8B4513 hover:bg-#6B3410"
                  data-testid="add-healer-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Healer
                </Button>
              </div>

              {healers.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No healers yet</p>
              ) : (
                <div className="space-y-4">
                  {healers.map((healer) => (
                    <Card key={healer.id} className="p-6" data-testid={`healer-${healer.user_id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{healer.user?.name || 'Unknown'}</h3>
                            <Badge>{healer.healer_type}</Badge>
                            {healer.is_approved ? (
                              <Badge className="bg-green-100 text-green-700">Approved</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                            )}
                            {healer.subscription_status === 'lifetime' && (
                              <Badge className="bg-purple-100 text-purple-700">Lifetime Free</Badge>
                            )}
                            {healer.subscription_status === 'trial' && (
                              <Badge className="bg-blue-100 text-blue-700">Trial</Badge>
                            )}
                            {healer.subscription_status === 'active' && (
                              <Badge className="bg-green-100 text-green-700">Subscribed</Badge>
                            )}
                            {(healer.subscription_status === 'expired' || healer.subscription_status === 'none') && healer.is_approved && (
                              <Badge className="bg-gray-100 text-gray-600">No Sub</Badge>
                            )}
                          </div>
                          <p className="text-gray-700 mb-2">{healer.location}</p>
                          <p className="text-sm text-gray-600 line-clamp-2">{healer.bio}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!healer.is_approved && (
                            <Button
                              size="sm"
                              onClick={() => handleApproveHealer(healer.user_id, true)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${healer.user_id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {healer.is_approved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveHealer(healer.user_id, false)}
                              data-testid={`unapprove-${healer.user_id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditHealer(healer)}
                            data-testid={`edit-${healer.user_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={healer.subscription_status === 'lifetime' ? 'default' : 'outline'}
                            onClick={() => handleToggleLifetime(healer.user_id, healer.subscription_status)}
                            className={healer.subscription_status === 'lifetime' ? 'bg-purple-600 hover:bg-purple-700' : 'text-purple-600 border-purple-300 hover:bg-purple-50'}
                            title={healer.subscription_status === 'lifetime' ? 'Revoke lifetime access' : 'Grant lifetime free access'}
                            data-testid={`lifetime-${healer.user_id}`}
                          >
                            <Gift className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteHealer(healer.user_id)}
                            data-testid={`delete-${healer.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="applications" className="mt-6">
              <h2 className="text-2xl font-bold mb-6">Healer Applications</h2>

              {applications.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No applications yet</p>
              ) : (
                <div className="space-y-4">
                  {applications.map((app) => (
                    <Card
                      key={app.id}
                      className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setSelectedApplication(app)}
                      data-testid={`application-${app.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900">{app.name}</h3>
                            <Badge
                              className={
                                app.status === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : app.status === 'rejected'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }
                            >
                              {app.status}
                            </Badge>
                          </div>
                          <p className="text-gray-700 mb-1">{app.business_name} - {app.healing_modality}</p>
                          <p className="text-sm text-gray-600">{app.email} • {app.phone_number}</p>
                          <p className="text-sm text-gray-600">{app.neighborhood}</p>
                        </div>
                        <p className="text-sm text-gray-500">{format(new Date(app.created_at), 'PPP')}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Revenue & Analytics Tab */}
            <TabsContent value="revenue" className="mt-6">
              <h2 className="text-2xl font-bold mb-6">Revenue & Analytics</h2>

              {loadingRevenue ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : revenue ? (
                <div className="space-y-8">
                  {/* Revenue Summary */}
                  <div className="grid md:grid-cols-4 gap-6">
                    <Card className="p-6">
                      <DollarSign className="w-8 h-8 text-green-600 mb-2" />
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-600">${revenue.total_revenue.toFixed(2)}</p>
                    </Card>
                    <Card className="p-6">
                      <CheckCircle className="w-8 h-8 text-blue-600 mb-2" />
                      <p className="text-sm text-gray-600">Paid Bookings</p>
                      <p className="text-3xl font-bold text-gray-900">{revenue.payment_status.paid}</p>
                    </Card>
                    <Card className="p-6">
                      <Calendar className="w-8 h-8 text-yellow-600 mb-2" />
                      <p className="text-sm text-gray-600">Pending Payments</p>
                      <p className="text-3xl font-bold text-yellow-600">{revenue.payment_status.pending}</p>
                    </Card>
                    <Card className="p-6">
                      <TrendingUp className="w-8 h-8 text-purple-600 mb-2" />
                      <p className="text-sm text-gray-600">Total Bookings</p>
                      <p className="text-3xl font-bold text-gray-900">{revenue.payment_status.total}</p>
                    </Card>
                  </div>

                  {/* Revenue by Healer Type */}
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4">Revenue by Healer Type</h3>
                    <div className="space-y-3">
                      {Object.entries(revenue.revenue_by_type).map(([type, amount]) => (
                        <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <span className="font-medium">{type}</span>
                          <span className="text-green-600 font-bold">${amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Top Earning Healers */}
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4">Top Earning Healers</h3>
                    <div className="space-y-3">
                      {revenue.top_healers.map((healer, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium">{healer.healer_name}</p>
                            <p className="text-sm text-gray-600">{healer.healer_type}</p>
                          </div>
                          <span className="text-green-600 font-bold">${healer.revenue.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Monthly Trends */}
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4">Monthly Revenue Trends</h3>
                    <div className="space-y-2">
                      {Object.entries(revenue.monthly_trends).reverse().slice(0, 12).map(([month, amount]) => (
                        <div key={month} className="flex items-center justify-between p-2 border-b">
                          <span className="text-gray-700">{month}</span>
                          <span className="font-semibold text-green-600">${amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              ) : (
                <p className="text-center py-8 text-gray-600">Click to load revenue data</p>
              )}
            </TabsContent>

            {/* User Management Tab */}
            <TabsContent value="users" className="mt-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">User Management</h2>
              </div>

              {/* Filters */}
              <div className="flex gap-4 mb-6">
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="max-w-sm"
                  data-testid="user-search-input"
                />
                <Select value={userRoleFilter || "all"} onValueChange={(val) => setUserRoleFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="max-w-xs" data-testid="user-role-filter">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="patient">Patients</SelectItem>
                    <SelectItem value="healer">Healers</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchUsers} data-testid="apply-user-filters">
                  Apply Filters
                </Button>
              </div>

              {loadingUsers ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : users.length === 0 ? (
                <p className="text-center py-8 text-gray-600">No users found</p>
              ) : (
                <div className="space-y-4">
                  {users.map((user) => (
                    <Card key={user.id} className="p-6" data-testid={`user-${user.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold">{user.name}</h3>
                            <Badge className="capitalize">{user.role}</Badge>
                            {user.suspended && (
                              <Badge className="bg-red-100 text-red-700">Suspended</Badge>
                            )}
                          </div>
                          <p className="text-gray-600">{user.email}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            Joined: {format(new Date(user.created_at), 'PPP')} • {user.booking_count} bookings
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewUserDetails(user.id)}
                            data-testid={`view-user-${user.id}`}
                          >
                            View Details
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant={user.suspended ? "default" : "destructive"}
                              onClick={() => handleSuspendUser(user.id, !user.suspended)}
                              data-testid={`suspend-user-${user.id}`}
                            >
                              {user.suspended ? (
                                <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                              ) : (
                                <><Ban className="w-4 h-4 mr-1" /> Suspend</>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Emails Tab */}
            <TabsContent value="emails" className="mt-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Email Notifications</h2>
              </div>

              <div className="flex gap-4 mb-6">
                <Select value={emailTypeFilter || "all"} onValueChange={(val) => setEmailTypeFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="max-w-xs" data-testid="email-type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="booking_confirmation">Booking Confirmation</SelectItem>
                    <SelectItem value="payment_receipt">Payment Receipt</SelectItem>
                    <SelectItem value="application_received">Application Received</SelectItem>
                    <SelectItem value="healer_notification">Healer Notification</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchEmails} data-testid="apply-email-filters">
                  Apply Filter
                </Button>
              </div>

              {loadingEmails ? (
                <div className="flex justify-center py-12">
                  <div className="spinner"></div>
                </div>
              ) : emails.length === 0 ? (
                <p className="text-center py-8 text-gray-600">No emails found</p>
              ) : (
                <div className="space-y-4">
                  {emails.map((email) => (
                    <Card key={email.id} className="p-6" data-testid={`email-${email.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {email.sent ? (
                              <MailOpen className="w-5 h-5 text-green-600" />
                            ) : (
                              <Mail className="w-5 h-5 text-yellow-600" />
                            )}
                            <h3 className="text-lg font-bold">{email.subject}</h3>
                            <Badge className={email.sent ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {email.sent ? 'Sent' : 'Pending'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {email.email_type?.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">To: {email.to_email}</p>
                          <p className="text-sm text-gray-500 whitespace-pre-wrap line-clamp-3">{email.body}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            Created: {format(new Date(email.created_at), 'PPP p')}
                            {email.sent_at && ` • Sent: ${format(new Date(email.sent_at), 'PPP p')}`}
                          </p>
                        </div>
                        {!email.sent && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkEmailSent(email.id)}
                            className="bg-green-600 hover:bg-green-700 ml-4"
                            data-testid={`mark-sent-${email.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Mark Sent
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Concerns Tab */}
            <TabsContent value="concerns" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold">Search Concerns</h2>
                  <p className="text-sm text-gray-600 mt-1">Manage the symptom/concern → modality mappings that power the homepage search. No AI — rules-based matching.</p>
                </div>
                <Button onClick={() => openConcernDialog(null)} data-testid="add-concern-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Concern
                </Button>
              </div>

              {loadingConcerns ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : concerns.length === 0 ? (
                <p className="text-center py-8 text-gray-600">No concerns yet. Add your first one to power the search.</p>
              ) : (
                <div className="space-y-3">
                  {concerns.map((c) => (
                    <Card key={c.id} className="p-4" data-testid={`concern-row-${c.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-stone-900">{c.concern}</h3>
                            {c.is_active === false && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                          </div>
                          <p className="text-xs text-stone-500 mb-2">
                            <strong>Keywords:</strong> {(c.keywords || []).join(', ') || '—'}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {(c.modalities || []).slice(0, 8).map((m, i) => (
                              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full border ${m.weight >= 3 ? 'bg-amber-50 border-amber-300 text-amber-800' : m.weight === 2 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-stone-50 border-stone-300 text-stone-700'}`}>
                                {m.modality} · {m.weight === 3 ? 'High' : m.weight === 2 ? 'Strong' : 'Moderate'}
                              </span>
                            ))}
                            {(c.modalities || []).length > 8 && <span className="text-xs text-stone-500">+{c.modalities.length - 8} more</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" variant="outline" onClick={() => openConcernDialog(c)} data-testid={`edit-concern-${c.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteConcern(c.id, c.concern)} data-testid={`delete-concern-${c.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Add/Edit Healer Dialog */}
      <Dialog open={showHealerDialog} onOpenChange={setShowHealerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="healer-dialog">
          <DialogHeader>
            <DialogTitle>{editingHealer ? 'Edit Healer' : 'Add Healer'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveHealer} className="space-y-4 py-4">
            <div>
              <Label htmlFor="healer_type">Healer Type *</Label>
              <Select
                value={healerForm.healer_type}
                onValueChange={(value) => setHealerForm({ ...healerForm, healer_type: value })}
              >
                <SelectTrigger data-testid="healer-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {healerTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                value={healerForm.bio}
                onChange={(e) => setHealerForm({ ...healerForm, bio: e.target.value })}
                rows={4}
                required
                data-testid="bio-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={healerForm.location}
                  onChange={(e) => setHealerForm({ ...healerForm, location: e.target.value })}
                  required
                  data-testid="location-input"
                />
              </div>
              <div>
                <Label htmlFor="years_experience">Years of Experience *</Label>
                <Input
                  id="years_experience"
                  type="number"
                  value={healerForm.years_experience}
                  onChange={(e) => setHealerForm({ ...healerForm, years_experience: e.target.value })}
                  required
                  data-testid="years-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="hourly_rate">Hourly Rate ($) *</Label>
              <Input
                id="hourly_rate"
                type="number"
                step="0.01"
                value={healerForm.hourly_rate}
                onChange={(e) => setHealerForm({ ...healerForm, hourly_rate: e.target.value })}
                required
                data-testid="rate-input"
              />
            </div>

            <div>
              <Label htmlFor="specialties">Specialties (comma-separated)</Label>
              <Input
                id="specialties"
                value={healerForm.specialties}
                onChange={(e) => setHealerForm({ ...healerForm, specialties: e.target.value })}
                placeholder="e.g. Pain Management, Stress Relief"
                data-testid="specialties-input"
              />
            </div>

            <div>
              <Label htmlFor="languages">Languages (comma-separated)</Label>
              <Input
                id="languages"
                value={healerForm.languages}
                onChange={(e) => setHealerForm({ ...healerForm, languages: e.target.value })}
                placeholder="e.g. English, Spanish"
                data-testid="languages-input"
              />
            </div>

            <div>
              <Label htmlFor="insurance">Insurance Accepted (comma-separated)</Label>
              <Input
                id="insurance"
                value={healerForm.insurance_accepted}
                onChange={(e) => setHealerForm({ ...healerForm, insurance_accepted: e.target.value })}
                placeholder="e.g. Blue Cross, Aetna"
                data-testid="insurance-input"
              />
            </div>

            <div>
              <Label htmlFor="profile_image">Profile Image URL</Label>
              <Input
                id="profile_image"
                type="url"
                value={healerForm.profile_image}
                onChange={(e) => setHealerForm({ ...healerForm, profile_image: e.target.value })}
                placeholder="https://"
                data-testid="image-input"
              />
            </div>

            <Button type="submit" className="w-full bg-#8B4513 hover:bg-#6B3410" data-testid="save-healer-btn">
              {editingHealer ? 'Update Healer' : 'Add Healer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Detail Dialog */}
      {selectedApplication && (
        <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail-dialog">
            <DialogHeader>
              <DialogTitle>Application Details</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <p className="font-semibold">{selectedApplication.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Business Name</Label>
                  <p className="font-semibold">{selectedApplication.business_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Healing Modality</Label>
                  <p className="font-semibold">{selectedApplication.healing_modality}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Phone</Label>
                  <p className="font-semibold">{selectedApplication.phone_number}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-semibold">{selectedApplication.email}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Neighborhood</Label>
                  <p className="font-semibold">{selectedApplication.neighborhood}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Address</Label>
                <p className="font-semibold">{selectedApplication.address}</p>
              </div>

              <div>
                <Label className="text-gray-600">Website</Label>
                <p className="font-semibold">{selectedApplication.website || 'N/A'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Instagram</Label>
                  <p className="font-semibold">{selectedApplication.instagram || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Facebook</Label>
                  <p className="font-semibold">{selectedApplication.facebook || 'N/A'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Twitter/X</Label>
                  <p className="font-semibold">{selectedApplication.twitter || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600">LinkedIn</Label>
                  <p className="font-semibold">{selectedApplication.linkedin || 'N/A'}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Additional Information</Label>
                <p className="font-semibold whitespace-pre-wrap">{selectedApplication.other_info || 'N/A'}</p>
              </div>

              <div className="flex gap-3 pt-4">
                {selectedApplication.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'approved')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="approve-application-btn"
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'rejected')}
                      variant="destructive"
                      className="flex-1"
                      data-testid="reject-application-btn"
                    >
                      Reject
                    </Button>
                  </>
                )}
                <Button
                  onClick={() => handleDeleteApplication(selectedApplication.id)}
                  variant="outline"
                  data-testid="delete-application-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* User Details Dialog */}
      {selectedUser && (
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <p className="font-semibold">{selectedUser.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-semibold">{selectedUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Role</Label>
                  <p className="font-semibold capitalize">{selectedUser.role}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Status</Label>
                  <p className="font-semibold">{selectedUser.suspended ? 'Suspended' : 'Active'}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Joined</Label>
                <p className="font-semibold">{format(new Date(selectedUser.created_at), 'PPP')}</p>
              </div>

              {selectedUser.healer_profile && (
                <div className="border-t pt-4">
                  <h3 className="font-bold mb-2">Healer Profile</h3>
                  <p className="text-sm"><strong>Type:</strong> {selectedUser.healer_profile.healer_type}</p>
                  <p className="text-sm"><strong>Location:</strong> {selectedUser.healer_profile.location}</p>
                  <p className="text-sm"><strong>Rate:</strong> ${selectedUser.healer_profile.hourly_rate}/hr</p>
                  <p className="text-sm"><strong>Approved:</strong> {selectedUser.healer_profile.is_approved ? 'Yes' : 'No'}</p>
                </div>
              )}

              <div className="border-t pt-4">
                <h3 className="font-bold mb-2">Bookings ({selectedUser.bookings?.length || 0})</h3>
                {selectedUser.bookings && selectedUser.bookings.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedUser.bookings.slice(0, 10).map((booking) => (
                      <div key={booking.id} className="text-sm p-2 bg-gray-50 rounded">
                        <p><strong>Date:</strong> {format(new Date(booking.date), 'PPP')}</p>
                        <p><strong>Status:</strong> {booking.status} • Payment: {booking.payment_status}</p>
                        <p><strong>Amount:</strong> ${booking.total_amount}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No bookings yet</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Concern Add/Edit Dialog */}
      <Dialog open={showConcernDialog} onOpenChange={setShowConcernDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="concern-dialog">
          <DialogHeader>
            <DialogTitle>{editingConcern ? 'Edit Concern' : 'Add Concern'}</DialogTitle>
            <DialogDescription>
              This powers the "What are you experiencing?" search on the homepage. When a visitor types anything matching this concern or its keywords, we'll show these modalities.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveConcern} className="space-y-4">
            <div>
              <Label htmlFor="concern-name">Concern name *</Label>
              <Input
                id="concern-name"
                value={concernForm.concern}
                onChange={(e) => setConcernForm({ ...concernForm, concern: e.target.value })}
                placeholder="e.g., Anxiety"
                required
                data-testid="concern-name-input"
              />
            </div>

            <div>
              <Label htmlFor="concern-keywords">Keywords / synonyms (comma-separated)</Label>
              <Input
                id="concern-keywords"
                value={concernForm.keywords}
                onChange={(e) => setConcernForm({ ...concernForm, keywords: e.target.value })}
                placeholder="anxious, worry, panic, on edge, nervous..."
                data-testid="concern-keywords-input"
              />
              <p className="text-xs text-stone-500 mt-1">These are the alternate phrases visitors might type. Search matches against all of them.</p>
            </div>

            <div>
              <Label htmlFor="concern-modalities">Modalities *</Label>
              <Textarea
                id="concern-modalities"
                rows={7}
                value={concernForm.modalities_text}
                onChange={(e) => setConcernForm({ ...concernForm, modalities_text: e.target.value })}
                placeholder={"One per line, format: Modality Name | weight | short reason\n\nExamples:\nSomatic Practitioner | 3 | Helps calm the nervous system\nBreathwork Facilitator | 3 | Regulates the anxiety response\nAcupuncturist | 2 | TCM approach to calming"}
                required
                data-testid="concern-modalities-input"
                className="font-mono text-sm"
              />
              <p className="text-xs text-stone-500 mt-1">
                <strong>Weight:</strong> 3 = High match (top recommendation), 2 = Strong match, 1 = Moderate match. Format: <code>Modality | weight | reason</code>
              </p>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={concernForm.is_active}
                onChange={(e) => setConcernForm({ ...concernForm, is_active: e.target.checked })}
                data-testid="concern-active-checkbox"
              />
              <span className="text-sm">Active (visible in search)</span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="save-concern-btn">
                {editingConcern ? 'Save Changes' : 'Add Concern'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowConcernDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;